import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

import { RestApiService } from '../../services';
import Constants from '../../constants/constants';
import { environment } from '../../../../environments/environment';

/**
 * Reward Banner Component
 * Displays energy price cap information and promotional rewards
 * Shows banner with details popup functionality
 */
@Component({
  selector: 'app-reward-banner',
  imports: [CommonModule],
  templateUrl: './reward-banner.component.html',
  styleUrl: './reward-banner.component.scss'
})
export class RewardBannerComponent implements OnInit, OnDestroy {
  @Input('isQuotePage') isQuotePage: boolean = true;
  energyBanner: any = null;
  showBannerDetailsPopup: boolean = false;
  tenancyBanner!: any;
  bannerPlacement!: string;
  private destroy$ = new Subject<void>();

  constructor(
    private restAPI: RestApiService,
    private router: Router
  ) {}
  
  ngOnInit(): void {
    // Set initial banner placement
    this.updateBannerPlacement(this.router.url);
    
    // Listen to route changes
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.updateBannerPlacement(event.urlAfterRedirects || event.url);
        // Refetch banner for new route
        this.getEnergyBanner();
      });
    
    this.getEnergyBanner();
  }

  /**
   * Updates banner placement based on the current route
   * @param url - The current route URL
   */
  private updateBannerPlacement(url: string): void {
    this.bannerPlacement = Constants.getBannerPlacementByRoute(url);
    this.energyBanner = this.tenancyBanner?.find((banner: any) => 
      banner?.placetoDisplay?.includes(this.bannerPlacement)
    ) || null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Fetches energy banner data from the API
   */
  getEnergyBanner(): void {
      this.restAPI.get(`/v1/ui-configuration?acquisitionParty=${environment.SUB_MODULE}`).subscribe({
      next: (response: any) => {
        if (response && response.tenancyBanner) {
          this.tenancyBanner = response.tenancyBanner;
          this.energyBanner = response?.tenancyBanner?.find((banner: any) => 
            banner?.placetoDisplay?.includes(this.bannerPlacement)
          ) || null;

          if (this.energyBanner?.scriptSource) {
            // Parse and set the scriptSource for styling
            this.energyBanner['scriptSource'] = JSON.parse(this.energyBanner.scriptSource.replace(/'/g, '"'));
          }

          // Process terms and conditions with replacement links
          if (this.energyBanner?.moreDetails?.tnc) {
            this.energyBanner.moreDetails.tnc?.replacementDisplayText?.map((item: any, index: number) => {
              if (item.url) {
                this.energyBanner.moreDetails.tnc.displayText = this.energyBanner.moreDetails.tnc.displayText.replace(`$${index + 1}`, `<a href="${item.url}" target="_blank" class="text-sm text-sp-link hover:underline whitespace-nowrap font-bold">${item.displayText}</a>`);
              }
            });
          }
        }
      },
      error: (error: any) => {
        console.error('Error fetching energy banner:', error);
      }
    });
  }

  /**
   * Opens the banner details popup
   * @param bannerDetails - The banner data to display
   */
  openBannerDetails(bannerDetails: any): void {
    if (bannerDetails) {
      this.showBannerDetailsPopup = true;
    }
  }

  /**
   * Closes the banner details popup
   */
  closeBannerDetails(): void {
    this.showBannerDetailsPopup = false;
  }
}
